/**
 * Spot REST API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { ConfigurationRestAPI, RestApiResponse, sendRequest, TimeUnit } from '@binance/common';
import { AccountApi } from './modules/account-api';
import { GeneralApi } from './modules/general-api';
import { MarketApi } from './modules/market-api';
import { TradeApi } from './modules/trade-api';

import type {
    AccountCommissionRequest,
    AllOrderListRequest,
    AllOrdersRequest,
    GetAccountRequest,
    GetOpenOrdersRequest,
    GetOrderRequest,
    GetOrderListRequest,
    MyAllocationsRequest,
    MyFiltersRequest,
    MyPreventedMatchesRequest,
    MyTradesRequest,
    OpenOrderListRequest,
    OrderAmendmentsRequest,
    RateLimitOrderRequest,
} from './modules/account-api';
import type { ExchangeInfoRequest, ExecutionRulesRequest } from './modules/general-api';
import type {
    AggTradesRequest,
    AvgPriceRequest,
    DepthRequest,
    GetTradesRequest,
    HistoricalBlockTradesRequest,
    HistoricalTradesRequest,
    KlinesRequest,
    ReferencePriceRequest,
    ReferencePriceCalculationRequest,
    TickerRequest,
    Ticker24hrRequest,
    TickerBookTickerRequest,
    TickerPriceRequest,
    TickerTradingDayRequest,
    UiKlinesRequest,
} from './modules/market-api';
import type {
    DeleteOpenOrdersRequest,
    DeleteOrderRequest,
    DeleteOrderListRequest,
    NewOrderRequest,
    OrderAmendKeepPriorityRequest,
    OrderCancelReplaceRequest,
    OrderListOcoRequest,
    OrderListOpoRequest,
    OrderListOpocoRequest,
    OrderListOtoRequest,
    OrderListOtocoRequest,
    OrderOcoRequest,
    OrderTestRequest,
    SorOrderRequest,
    SorOrderTestRequest,
} from './modules/trade-api';

import type {
    AccountCommissionResponse,
    AllOrderListResponse,
    AllOrdersResponse,
    GetAccountResponse,
    GetOpenOrdersResponse,
    GetOrderResponse,
    GetOrderListResponse,
    MyAllocationsResponse,
    MyFiltersResponse,
    MyPreventedMatchesResponse,
    MyTradesResponse,
    OpenOrderListResponse,
    OrderAmendmentsResponse,
    RateLimitOrderResponse,
} from './types';
import type { ExchangeInfoResponse, ExecutionRulesResponse, TimeResponse } from './types';
import type {
    AggTradesResponse,
    AvgPriceResponse,
    DepthResponse,
    GetTradesResponse,
    HistoricalBlockTradesResponse,
    HistoricalTradesResponse,
    KlinesResponse,
    ReferencePriceResponse,
    ReferencePriceCalculationResponse,
    TickerResponse,
    Ticker24hrResponse,
    TickerBookTickerResponse,
    TickerPriceResponse,
    TickerTradingDayResponse,
    UiKlinesResponse,
} from './types';
import type {
    DeleteOpenOrdersResponse,
    DeleteOrderResponse,
    DeleteOrderListResponse,
    NewOrderResponse,
    OrderAmendKeepPriorityResponse,
    OrderCancelReplaceResponse,
    OrderListOcoResponse,
    OrderListOpoResponse,
    OrderListOpocoResponse,
    OrderListOtoResponse,
    OrderListOtocoResponse,
    OrderOcoResponse,
    OrderTestResponse,
    SorOrderResponse,
    SorOrderTestResponse,
} from './types';

export class RestAPI {
    private configuration: ConfigurationRestAPI;
    private accountApi: AccountApi;
    private generalApi: GeneralApi;
    private marketApi: MarketApi;
    private tradeApi: TradeApi;

    constructor(configuration: ConfigurationRestAPI) {
        this.configuration = configuration;
        this.accountApi = new AccountApi(configuration);
        this.generalApi = new GeneralApi(configuration);
        this.marketApi = new MarketApi(configuration);
        this.tradeApi = new TradeApi(configuration);
    }

    /**
     * Generic function to send a request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     * @param timeUnit - The time unit for the request.
     * @returns A promise resolving to the response data object.
     */
    sendRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {},
        timeUnit: TimeUnit = this.configuration?.timeUnit as TimeUnit
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            timeUnit
        );
    }

    /**
     * Generic function to send a signed request.
     * @param endpoint - The API endpoint to call.
     * @param method - HTTP method to use (GET, POST, DELETE, etc.).
     * @param queryParams - Query parameters for the request.
     * @param bodyParams - Body parameters for the request.
     * @param timeUnit - The time unit for the request.
     * @returns A promise resolving to the response data object.
     */
    sendSignedRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH',
        queryParams: Record<string, unknown> = {},
        bodyParams: Record<string, unknown> = {},
        timeUnit: TimeUnit = this.configuration?.timeUnit as TimeUnit
    ): Promise<RestApiResponse<T>> {
        return sendRequest<T>(
            this.configuration,
            endpoint,
            method,
            queryParams,
            bodyParams,
            undefined,
            timeUnit,
            { isSigned: true }
        );
    }

    /**
     * Get current account commission rates.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Commission Rates (USER_DATA)
     * @param {AccountCommissionRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AccountCommissionResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#account-commission Binance API Documentation}
     */
    accountCommission(
        requestParameters: AccountCommissionRequest
    ): Promise<RestApiResponse<AccountCommissionResponse>> {
        return this.accountApi.accountCommission(requestParameters);
    }

    /**
     * Retrieves all order lists based on provided optional parameters.
     *
     * Note that the time between `startTime` and `endTime` can't be longer
     * than 24 hours.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query all Order lists (USER_DATA)
     * @param {AllOrderListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AllOrderListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#all-order-list Binance API Documentation}
     */
    allOrderList(
        requestParameters: AllOrderListRequest = {}
    ): Promise<RestApiResponse<AllOrderListResponse>> {
        return this.accountApi.allOrderList(requestParameters);
    }

    /**
     * Get all account orders; active, canceled, or filled.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * - If `orderId` is set, it will get orders >= that `orderId`. Otherwise most recent orders are returned.
     * - For some historical orders `cummulativeQuoteQty` will be < 0, meaning the data is not available at this time.
     * - If `startTime` and/or `endTime` provided, `orderId` is not required.
     * - The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary All orders (USER_DATA)
     * @param {AllOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AllOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#all-orders Binance API Documentation}
     */
    allOrders(requestParameters: AllOrdersRequest): Promise<RestApiResponse<AllOrdersResponse>> {
        return this.accountApi.allOrders(requestParameters);
    }

    /**
     * Get current account information.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * @summary Account information (USER_DATA)
     * @param {GetAccountRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetAccountResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#get-account Binance API Documentation}
     */
    getAccount(
        requestParameters: GetAccountRequest = {}
    ): Promise<RestApiResponse<GetAccountResponse>> {
        return this.accountApi.getAccount(requestParameters);
    }

    /**
     * Get all open orders on a symbol. **Careful** when accessing this with no symbol.
     *
     * Weight: 6 for a single symbol; 80 when the symbol parameter is omitted
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * - If the symbol is not sent, orders for all symbols will be returned in an array.
     *
     * @summary Current open orders (USER_DATA)
     * @param {GetOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#get-open-orders Binance API Documentation}
     */
    getOpenOrders(
        requestParameters: GetOpenOrdersRequest = {}
    ): Promise<RestApiResponse<GetOpenOrdersResponse>> {
        return this.accountApi.getOpenOrders(requestParameters);
    }

    /**
     * Check an order's status.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     * - Either `orderId` or `origClientOrderId` must be sent.
     * - If both `orderId` and `origClientOrderId` are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     * - For some historical orders `cummulativeQuoteQty` will be < 0, meaning the data is not available at this time.
     *
     * @summary Query order (USER_DATA)
     * @param {GetOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#get-order Binance API Documentation}
     */
    getOrder(requestParameters: GetOrderRequest): Promise<RestApiResponse<GetOrderResponse>> {
        return this.accountApi.getOrder(requestParameters);
    }

    /**
     * Retrieves a specific order list based on provided optional parameters.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Order list (USER_DATA)
     * @param {GetOrderListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetOrderListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#get-order-list Binance API Documentation}
     */
    getOrderList(
        requestParameters: GetOrderListRequest = {}
    ): Promise<RestApiResponse<GetOrderListResponse>> {
        return this.accountApi.getOrderList(requestParameters);
    }

    /**
     * Retrieves allocations resulting from SOR order placement.
     *
     * Weight(IP): 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database"
     *
     * Supported parameter combinations:
     *
     * Parameters                                  | Response |
     * ------------------------------------------- | -------- |
     * `symbol`                                    | allocations from oldest to newest |
     * `symbol` + `startTime`                      | oldest allocations since `startTime` |
     * `symbol` + `endTime`                        | newest allocations until `endTime` |
     * `symbol` + `startTime` + `endTime`          | allocations within the time range |
     * `symbol` + `fromAllocationId`               | allocations by allocation ID |
     * `symbol` + `orderId`                        | allocations related to an order starting with oldest |
     * `symbol` + `orderId` + `fromAllocationId`   | allocations related to an order by allocation ID |
     *
     **Note:** The time between `startTime` and `endTime` can't be longer than 24 hours.
     *
     * @summary Query Allocations (USER_DATA)
     * @param {MyAllocationsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<MyAllocationsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#my-allocations Binance API Documentation}
     */
    myAllocations(
        requestParameters: MyAllocationsRequest
    ): Promise<RestApiResponse<MyAllocationsResponse>> {
        return this.accountApi.myAllocations(requestParameters);
    }

    /**
     * Retrieves the list of filters relevant to an account on a given symbol. This is the only endpoint that shows if an account has `MAX_ASSET` filters applied to it.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query relevant filters (USER_DATA)
     * @param {MyFiltersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<MyFiltersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#my-filters Binance API Documentation}
     */
    myFilters(requestParameters: MyFiltersRequest): Promise<RestApiResponse<MyFiltersResponse>> {
        return this.accountApi.myFilters(requestParameters);
    }

    /**
     * Displays the list of orders that were expired due to STP.
     *
     * These are the combinations supported:
     * - `symbol` + `preventedMatchId`
     * - `symbol` + `orderId`
     * - `symbol` + `orderId` + `fromPreventedMatchId` (`limit` will default to 500)
     * - `symbol` + `orderId` + `fromPreventedMatchId` + `limit`
     *
     * Weight: Case                            | Weight
     * ----                            | -----
     * If `symbol` is invalid          | 2
     * Querying by `preventedMatchId`  | 2
     * Querying by `orderId`           | 20
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Prevented Matches (USER_DATA)
     * @param {MyPreventedMatchesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<MyPreventedMatchesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#my-prevented-matches Binance API Documentation}
     */
    myPreventedMatches(
        requestParameters: MyPreventedMatchesRequest
    ): Promise<RestApiResponse<MyPreventedMatchesResponse>> {
        return this.accountApi.myPreventedMatches(requestParameters);
    }

    /**
     * Get trades for a specific account and symbol.
     *
     * Weight: Condition| Weight|
     * ---| ---
     * |Without orderId|20|
     * |With orderId|5|
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory => Database
     *
     **Notes:**:
     * - If `fromId` is set, it will get trades >= that `fromId`. Otherwise most recent trades are returned.
     * - The time between `startTime` and `endTime` can't be longer than 24 hours.
     * - These are the supported combinations of all parameters:
     * - `symbol`
     * - `symbol` + `orderId`
     * - `symbol` + `startTime`
     * - `symbol` + `endTime`
     * - `symbol` + `fromId`
     * - `symbol` + `startTime` + `endTime`
     * - `symbol`+ `orderId` + `fromId`
     *
     * @summary Account trade list (USER_DATA)
     * @param {MyTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<MyTradesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#my-trades Binance API Documentation}
     */
    myTrades(requestParameters: MyTradesRequest): Promise<RestApiResponse<MyTradesResponse>> {
        return this.accountApi.myTrades(requestParameters);
    }

    /**
     * Query Open Order lists
     *
     * Weight(IP): 6
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory -> Database
     *
     * @summary Query Open Order lists (USER_DATA)
     * @param {OpenOrderListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OpenOrderListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#open-order-list Binance API Documentation}
     */
    openOrderList(
        requestParameters: OpenOrderListRequest = {}
    ): Promise<RestApiResponse<OpenOrderListResponse>> {
        return this.accountApi.openOrderList(requestParameters);
    }

    /**
     * Queries all amendments of a single order.
     *
     * Weight(IP): 4
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Query Order Amendments (USER_DATA)
     * @param {OrderAmendmentsRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderAmendmentsResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#order-amendments Binance API Documentation}
     */
    orderAmendments(
        requestParameters: OrderAmendmentsRequest
    ): Promise<RestApiResponse<OrderAmendmentsResponse>> {
        return this.accountApi.orderAmendments(requestParameters);
    }

    /**
     * Displays the user's unfilled order count for all intervals.
     *
     * Weight(IP): 40
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Unfilled Order Count (USER_DATA)
     * @param {RateLimitOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<RateLimitOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/account#rate-limit-order Binance API Documentation}
     */
    rateLimitOrder(
        requestParameters: RateLimitOrderRequest = {}
    ): Promise<RestApiResponse<RateLimitOrderResponse>> {
        return this.accountApi.rateLimitOrder(requestParameters);
    }

    /**
     * Current exchange trading rules and symbol information
     *
     * Weight(IP): 20
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Notes:**
     * If the value provided to `symbol` or `symbols` do not exist, the endpoint will throw an error saying the symbol is invalid.
     * All parameters are optional.
     * `permissions` can support single or multiple values (e.g. `SPOT`, `["MARGIN","LEVERAGED"]`). This cannot be used in combination with `symbol` or `symbols`.
     * If `permissions` parameter not provided, all symbols that have either `SPOT`, `MARGIN`, or `LEVERAGED` permission will be exposed.
     * To display symbols with any permission you need to specify them explicitly in `permissions`: (e.g. `["SPOT","MARGIN",...]`.). See Account and Symbol Permissions for the full list.
     *
     **Examples of Symbol Permissions Interpretation from the Response:**
     *
     * `[["A","B"]]` means you may place an order if your account has either permission "A" **or** permission "B".
     * `[["A"],["B"]]` means you can place an order if your account has permission "A" **and** permission "B".
     * `[["A"],["B","C"]]` means you can place an order if your account has permission "A" **and** permission "B" or permission "C". (Inclusive or is applied here, not exclusive or, so your account may have both permission "B" and permission "C".)
     *
     * @summary Exchange information
     * @param {ExchangeInfoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ExchangeInfoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#exchange-info Binance API Documentation}
     */
    exchangeInfo(
        requestParameters: ExchangeInfoRequest = {}
    ): Promise<RestApiResponse<ExchangeInfoResponse>> {
        return this.generalApi.exchangeInfo(requestParameters);
    }

    /**
     * Query execution rules for symbols.
     *
     * Weight: Parameter | Weight
     * --- | ---
     * `symbol` | 2
     * `symbols` | 2 for each `symbol`, capped at a max of 40
     * `symbolStatus` | 40
     * None | 40
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     **Note:**: No combination of multiple parameters is allowed.
     *
     * @summary Query Execution Rules
     * @param {ExecutionRulesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ExecutionRulesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#execution-rules Binance API Documentation}
     */
    executionRules(
        requestParameters: ExecutionRulesRequest = {}
    ): Promise<RestApiResponse<ExecutionRulesResponse>> {
        return this.generalApi.executionRules(requestParameters);
    }

    /**
     * Test connectivity to the Rest API.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Test connectivity
     *
     * @returns {Promise<RestApiResponse<void>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#ping Binance API Documentation}
     */
    ping(): Promise<RestApiResponse<void>> {
        return this.generalApi.ping();
    }

    /**
     * Test connectivity to the Rest API and get the current server time.
     *
     * Weight(IP): 1
     *
     * Security Type: NONE
     *
     * @summary Check server time
     *
     * @returns {Promise<RestApiResponse<TimeResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/general#time Binance API Documentation}
     */
    time(): Promise<RestApiResponse<TimeResponse>> {
        return this.generalApi.time();
    }

    /**
     * Get compressed, aggregate trades. Trades that fill at the time, from the same taker order, with the same price will have the quantity aggregated.
     *
     * Weight(IP): 4
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * - If fromId, startTime, and endTime are not sent, the most recent aggregate trades will be returned.
     *
     * @summary Compressed/Aggregate trades list
     * @param {AggTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AggTradesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#agg-trades Binance API Documentation}
     */
    aggTrades(requestParameters: AggTradesRequest): Promise<RestApiResponse<AggTradesResponse>> {
        return this.marketApi.aggTrades(requestParameters);
    }

    /**
     * Current average price for a symbol.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Current average price
     * @param {AvgPriceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<AvgPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#avg-price Binance API Documentation}
     */
    avgPrice(requestParameters: AvgPriceRequest): Promise<RestApiResponse<AvgPriceResponse>> {
        return this.marketApi.avgPrice(requestParameters);
    }

    /**
     * Order book
     *
     * Weight: Adjusted based on the limit:
     *
     * |Limit|Request Weight
     * ------|-------
     * 1-100|  5
     * 101-500| 25
     * 501-1000| 50
     * 1001-5000| 250
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Order book
     * @param {DepthRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<DepthResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#depth Binance API Documentation}
     */
    depth(requestParameters: DepthRequest): Promise<RestApiResponse<DepthResponse>> {
        return this.marketApi.depth(requestParameters);
    }

    /**
     * Get recent trades.
     *
     * Weight(IP): 25
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Recent trades list
     * @param {GetTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<GetTradesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#get-trades Binance API Documentation}
     */
    getTrades(requestParameters: GetTradesRequest): Promise<RestApiResponse<GetTradesResponse>> {
        return this.marketApi.getTrades(requestParameters);
    }

    /**
     * Get block trades.
     *
     * Weight(IP): 25
     *
     * Security Type: MARKET_DATA
     *
     * Notes:
     * - Data Source: Database
     *
     * @summary Historical Block Trades (MARKET_DATA)
     * @param {HistoricalBlockTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<HistoricalBlockTradesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#historical-block-trades Binance API Documentation}
     */
    historicalBlockTrades(
        requestParameters: HistoricalBlockTradesRequest
    ): Promise<RestApiResponse<HistoricalBlockTradesResponse>> {
        return this.marketApi.historicalBlockTrades(requestParameters);
    }

    /**
     * Get older trades.
     *
     * Weight(IP): 25
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Old trade lookup
     * @param {HistoricalTradesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<HistoricalTradesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#historical-trades Binance API Documentation}
     */
    historicalTrades(
        requestParameters: HistoricalTradesRequest
    ): Promise<RestApiResponse<HistoricalTradesResponse>> {
        return this.marketApi.historicalTrades(requestParameters);
    }

    /**
     * Kline/candlestick bars for a symbol.
     * Klines are uniquely identified by their open time.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * Supported kline intervals (case-sensitive):
     *
     * Interval  | `interval` value
     * --------- | ----------------
     * seconds   | `1s`
     * minutes   | `1m`, `3m`, `5m`, `15m`, `30m`
     * hours     | `1h`, `2h`, `4h`, `6h`, `8h`, `12h`
     * days      | `1d`, `3d`
     * weeks     | `1w`
     * months    | `1M`
     *
     **Notes:**
     *
     * If `startTime` and `endTime` are not sent, the most recent klines are returned.
     * Supported values for `timeZone`:
     * Hours and minutes (e.g. `-1:00`, `05:45`)
     * Only hours (e.g. `0`, `8`, `4`)
     * Accepted range is strictly [-12:00 to +14:00] inclusive
     * If `timeZone` provided, kline intervals are interpreted in that timezone instead of UTC.
     * Note that `startTime` and `endTime` are always interpreted in UTC, regardless of `timeZone`.
     *
     * @summary Kline/Candlestick data
     * @param {KlinesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<KlinesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#klines Binance API Documentation}
     */
    klines(requestParameters: KlinesRequest): Promise<RestApiResponse<KlinesResponse>> {
        return this.marketApi.klines(requestParameters);
    }

    /**
     * Query the reference price for a symbol.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Reference Price
     * @param {ReferencePriceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ReferencePriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#reference-price Binance API Documentation}
     */
    referencePrice(
        requestParameters: ReferencePriceRequest
    ): Promise<RestApiResponse<ReferencePriceResponse>> {
        return this.marketApi.referencePrice(requestParameters);
    }

    /**
     * Describes how reference price is calculated for a given symbol.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query Reference Price Calculation
     * @param {ReferencePriceCalculationRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<ReferencePriceCalculationResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#reference-price-calculation Binance API Documentation}
     */
    referencePriceCalculation(
        requestParameters: ReferencePriceCalculationRequest
    ): Promise<RestApiResponse<ReferencePriceCalculationResponse>> {
        return this.marketApi.referencePriceCalculation(requestParameters);
    }

    /**
     * **Note:** This endpoint differs from `GET /api/v3/ticker/24hr`.
     *
     * The statistical time range of this endpoint can be up to 59999ms longer
     * than the requested `windowSize`.
     *
     * `openTime` starts at the beginning of a minute, while the end time is
     * the current time. Therefore, the actual interval can be up to 59999ms
     * longer than the requested window.
     *
     * For example, if `closeTime` is 1641287867099 (January 04, 2022
     * 09:17:47:099 UTC) and `windowSize` is `1d`, then `openTime` is
     * 1641201420000 (January 3, 2022, 09:17:00 UTC).
     *
     * Weight: 4 for each requested symbol regardless of windowSize.
     *
     * The weight for this request will cap at 200 once the number of `symbols` in the request is more than 50.
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * @summary Rolling window price change statistics
     * @param {TickerRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ticker Binance API Documentation}
     */
    ticker(requestParameters: TickerRequest = {}): Promise<RestApiResponse<TickerResponse>> {
        return this.marketApi.ticker(requestParameters);
    }

    /**
     * 24 hour rolling window price change statistics. **Careful** when accessing this with no symbol.
     *
     * Weight: <table>
     * <thead>
     * <tr>
     * <th>Parameter</th>
     * <th>Symbols Provided</th>
     * <th>Weight</th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td rowspan="2">symbol</td>
     * <td>1</td>
     * <td>2</td>
     * </tr>
     * <tr>
     * <td>symbol parameter is omitted</td>
     * <td>80</td>
     * </tr>
     * <tr>
     * <td rowspan="4">symbols</td>
     * <td>1-20</td>
     * <td>2</td>
     * </tr>
     * <tr>
     * <td>21-100</td>
     * <td>40</td>
     * </tr>
     * <tr>
     * <td>101 or more</td>
     * <td>80</td>
     * </tr>
     * <tr>
     * <td>symbols parameter is omitted</td>
     * <td>80</td>
     * </tr>
     * </tbody>
     * </table>
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary 24hr ticker price change statistics
     * @param {Ticker24hrRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<Ticker24hrResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ticker24hr Binance API Documentation}
     */
    ticker24hr(
        requestParameters: Ticker24hrRequest = {}
    ): Promise<RestApiResponse<Ticker24hrResponse>> {
        return this.marketApi.ticker24hr(requestParameters);
    }

    /**
     * Best price/qty on the order book for a symbol or symbols.
     *
     * Weight: |Parameter|Symbols Provided|Weight|
     * |---|---|---|
     * |symbol| 1 |2|
     * | |omitted| 4|
     * |symbols| Any |4|
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Symbol order book ticker
     * @param {TickerBookTickerRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TickerBookTickerResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ticker-book-ticker Binance API Documentation}
     */
    tickerBookTicker(
        requestParameters: TickerBookTickerRequest = {}
    ): Promise<RestApiResponse<TickerBookTickerResponse>> {
        return this.marketApi.tickerBookTicker(requestParameters);
    }

    /**
     * Latest price for a symbol or symbols.
     *
     * Weight: |Parameter|Symbols Provided|Weight|
     * |---|---|---|
     * |symbol| 1 |2|
     * | |omitted| 4|
     * |symbols| Any |4|
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Symbol price ticker
     * @param {TickerPriceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TickerPriceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ticker-price Binance API Documentation}
     */
    tickerPrice(
        requestParameters: TickerPriceRequest = {}
    ): Promise<RestApiResponse<TickerPriceResponse>> {
        return this.marketApi.tickerPrice(requestParameters);
    }

    /**
     * Price change statistics for a trading day.
     *
     * Weight: 4 for each requested symbol. The weight for this request will cap at 200 once the number of symbols in the request is more than 50.
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     **Notes:**:
     * - Supported values for `timeZone`:
     * - Hours and minutes (e.g. `-1:00`, `05:45`)
     * - Only hours (e.g. `0`, `8`, `4`)
     *
     * @summary Trading Day Ticker
     * @param {TickerTradingDayRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<TickerTradingDayResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ticker-trading-day Binance API Documentation}
     */
    tickerTradingDay(
        requestParameters: TickerTradingDayRequest = {}
    ): Promise<RestApiResponse<TickerTradingDayResponse>> {
        return this.marketApi.tickerTradingDay(requestParameters);
    }

    /**
     * The request is similar to klines having the same parameters and
     * response.
     *
     * `uiKlines` return modified kline data, optimized for presentation of
     * candlestick charts.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Database
     *
     * - If `startTime` and `endTime` are not sent, the most recent klines are returned.
     * - Supported values for `timeZone`:
     * - Hours and minutes (e.g. `-1:00`, `05:45`)
     * - Only hours (e.g. `0`, `8`, `4`)
     * - Accepted range is strictly [-12:00 to +14:00] inclusive
     * - If `timeZone` provided, kline intervals are interpreted in that timezone instead of UTC.
     * - Note that `startTime` and `endTime` are always interpreted in UTC, regardless of `timeZone`.
     *
     * @summary UIKlines
     * @param {UiKlinesRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<UiKlinesResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/market#ui-klines Binance API Documentation}
     */
    uiKlines(requestParameters: UiKlinesRequest): Promise<RestApiResponse<UiKlinesResponse>> {
        return this.marketApi.uiKlines(requestParameters);
    }

    /**
     * Cancels all active orders on a symbol.
     * This includes orders that are part of an order list.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Cancel All Open Orders on a Symbol (TRADE)
     * @param {DeleteOpenOrdersRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<DeleteOpenOrdersResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#delete-open-orders Binance API Documentation}
     */
    deleteOpenOrders(
        requestParameters: DeleteOpenOrdersRequest
    ): Promise<RestApiResponse<DeleteOpenOrdersResponse>> {
        return this.tradeApi.deleteOpenOrders(requestParameters);
    }

    /**
     * Cancel an active order.
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * - Either `orderId` or `origClientOrderId` must be sent.
     * - If both `orderId` and `origClientOrderId` are provided, the `orderId` is searched first, then the `origClientOrderId` from that result is checked against that order. If both conditions are not met the request will be rejected.
     * - The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel order (TRADE)
     * @param {DeleteOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<DeleteOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#delete-order Binance API Documentation}
     */
    deleteOrder(
        requestParameters: DeleteOrderRequest
    ): Promise<RestApiResponse<DeleteOrderResponse>> {
        return this.tradeApi.deleteOrder(requestParameters);
    }

    /**
     * Cancel an entire Order list
     *
     * Weight(IP): 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Notes:**
     * - Canceling an individual order from an order list will cancel the entire order list.
     * - If both orderListId and listClientOrderId parameters are provided, the orderListId is searched first, then the listClientOrderId from that result is checked against that order. If both conditions are not met the request will be rejected.
     *
     * @summary Cancel Order list (TRADE)
     * @param {DeleteOrderListRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<DeleteOrderListResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#delete-order-list Binance API Documentation}
     */
    deleteOrderList(
        requestParameters: DeleteOrderListRequest
    ): Promise<RestApiResponse<DeleteOrderListResponse>> {
        return this.tradeApi.deleteOrderList(requestParameters);
    }

    /**
     * Send in a new order.
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Some additional mandatory parameters based on order `type`:
     *
     * Type | Additional mandatory parameters | Additional Information
     * ------------ | ------------| ------
     * `LIMIT` | `timeInForce`, `quantity`, `price`|
     * `MARKET` | `quantity` or `quoteOrderQty`| `MARKET` orders using the `quantity` field specifies the amount of the `base asset` the user wants to buy or sell at the market price. <br/> E.g. MARKET order on BTCUSDT will specify how much BTC the user is buying or selling. <br/><br/> `MARKET` orders using `quoteOrderQty` specifies the amount the user wants to spend (when buying) or receive (when selling) the `quote` asset; the correct `quantity` will be determined based on the market liquidity and `quoteOrderQty`. <br/> E.g. Using the symbol BTCUSDT: <br/> `BUY` side, the order will buy as many BTC as `quoteOrderQty` USDT can. <br/> `SELL` side, the order will sell as much BTC needed to receive `quoteOrderQty` USDT.
     * `STOP_LOSS` | `quantity`, `stopPrice` or `trailingDelta`| This will execute a `MARKET` order when the conditions are met. (e.g. `stopPrice` is met or `trailingDelta` is activated)
     * `STOP_LOSS_LIMIT` | `timeInForce`, `quantity`,  `price`, `stopPrice` or `trailingDelta`
     * `TAKE_PROFIT` | `quantity`, `stopPrice` or `trailingDelta` | This will execute a `MARKET` order when the conditions are met. (e.g. `stopPrice` is met or `trailingDelta` is activated)
     * `TAKE_PROFIT_LIMIT` | `timeInForce`, `quantity`, `price`, `stopPrice` or `trailingDelta` |
     * `LIMIT_MAKER` | `quantity`, `price`| This is a `LIMIT` order that will be rejected if the order immediately matches and trades as a taker. <br/> This is also known as a POST-ONLY order.
     *
     *
     * Notes on using parameters for Pegged Orders:
     * These parameters are allowed for `LIMIT`, `LIMIT_MAKER`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT_LIMIT` orders.
     * If `pegPriceType` is specified, `price` becomes optional. Otherwise, it is still mandatory.
     * `pegPriceType=PRIMARY_PEG` means the primary peg, that is the best price on the same side of the order book as your order.
     * `pegPriceType=MARKET_PEG` means the market peg, that is the best price on the opposite side of the order book from your order.
     * Use `pegOffsetType` and `pegOffsetValue` to request a price level other than the best one. These parameters must be specified together.
     *
     * Other info:
     * Any `LIMIT` or `LIMIT_MAKER` type order can be made an iceberg order by sending an `icebergQty`.
     * Any order with an `icebergQty` MUST have `timeInForce` set to `GTC`.
     * For `STOP_LOSS`, `STOP_LOSS_LIMIT`, `TAKE_PROFIT_LIMIT` and `TAKE_PROFIT` orders, `trailingDelta` can be combined with `stopPrice`.
     * `MARKET` orders using `quoteOrderQty` will not break `LOT_SIZE` filter rules; the order will execute a `quantity` that will have the notional value as close as possible to `quoteOrderQty`. Trigger order price rules against market price for both MARKET and LIMIT versions:
     * Price above market price: `STOP_LOSS` `BUY`, `TAKE_PROFIT` `SELL`
     * Price below market price: `STOP_LOSS` `SELL`, `TAKE_PROFIT` `BUY`
     *
     * @summary New order (TRADE)
     * @param {NewOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<NewOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#new-order Binance API Documentation}
     */
    newOrder(requestParameters: NewOrderRequest): Promise<RestApiResponse<NewOrderResponse>> {
        return this.tradeApi.newOrder(requestParameters);
    }

    /**
     * Reduce the quantity of an existing open order.
     *
     * This adds 0 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read Order Amend Keep Priority FAQ to learn more.
     *
     * Weight(IP): 4
     *
     * Unfilled Order Count: 0
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary Order Amend Keep Priority (TRADE)
     * @param {OrderAmendKeepPriorityRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderAmendKeepPriorityResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-amend-keep-priority Binance API Documentation}
     */
    orderAmendKeepPriority(
        requestParameters: OrderAmendKeepPriorityRequest
    ): Promise<RestApiResponse<OrderAmendKeepPriorityResponse>> {
        return this.tradeApi.orderAmendKeepPriority(requestParameters);
    }

    /**
     * - Cancels an existing order and places a new order on the same symbol.
     * - Filters and Order Count are evaluated before the processing of the cancellation and order placement occurs.
     * - A new order that was not attempted (i.e. when `newOrderResult: NOT_ATTEMPTED`), will still increase the unfilled order count by 1.
     * - You can only cancel an individual order from an orderList using this endpoint, but the result is the same as canceling the entire orderList.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * Similar to `POST /api/v3/order`, additional mandatory parameters are determined by `type`.
     * Response format varies depending on whether the processing of the message succeeded, partially succeeded, or failed.
     *
     * <table>
     * <thead>
     * <tr>
     * <th colspan=3 align=left>Request</th>
     * <th colspan=3 align=left>Response</th>
     * </tr>
     * <tr>
     * <th><code>cancelReplaceMode</code></th>
     * <th><code>orderRateLimitExceededMode</code></th>
     * <th>Unfilled Order Count</th>
     * <th><code>cancelResult</code></th>
     * <th><code>newOrderResult</code></th>
     * <th><code>status</code></th>
     * </tr>
     * </thead>
     * <tbody>
     * <tr>
     * <td rowspan="11"><code>STOP_ON_FAILURE</code></td>
     * <td rowspan="6"><code>DO_NOTHING</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="3">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="5"><code>CANCEL_ONLY</code></td>
     * <td rowspan="3">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="2">Exceeds Limits</td>
     * <td>❌ <code>FAILURE</code></td>
     * <td>➖ <code>NOT_ATTEMPTED</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>429</code></td>
     * </tr>
     * <tr>
     * <td rowspan="16"><code>ALLOW_FAILURE</code></td>
     * <td rowspan="8"><code>DO_NOTHING</code></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td rowspan="8"><CODE>CANCEL_ONLY</CODE></td>
     * <td rowspan="4">Within Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>200</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * <tr>
     * <td rowspan="4">Exceeds Limits</td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right><code>N/A</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>400</code></td>
     * </tr>
     * <tr>
     * <td>❌ <code>FAILURE</code></td>
     * <td>✅ <code>SUCCESS</code></td>
     * <td align=right>N/A</td>
     * </tr>
     * <tr>
     * <td>✅ <code>SUCCESS</code></td>
     * <td>❌ <code>FAILURE</code></td>
     * <td align=right><code>409</code></td>
     * </tr>
     * </tbody>
     * </table>
     *
     **Notes:**
     * - The performance for canceling an order (single cancel or as part of a cancel-replace) is always better when only `orderId` is sent. Sending `origClientOrderId` or both `orderId` + `origClientOrderId` will be slower.
     *
     * @summary Cancel an Existing Order and Send a New Order (TRADE)
     * @param {OrderCancelReplaceRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderCancelReplaceResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-cancel-replace Binance API Documentation}
     */
    orderCancelReplace(
        requestParameters: OrderCancelReplaceRequest
    ): Promise<RestApiResponse<OrderCancelReplaceResponse>> {
        return this.tradeApi.orderCancelReplace(requestParameters);
    }

    /**
     * Send in an one-cancels-the-other (OCO) pair, where activation of one
     * order immediately cancels the other.
     *
     * - An OCO has 2 orders called the **above order** and **below order**.
     * - One of the orders must be a `LIMIT_MAKER/TAKE_PROFIT/TAKE_PROFIT_LIMIT` order and the other must be `STOP_LOSS` or `STOP_LOSS_LIMIT` order.
     * - Price restrictions
     * - If the OCO is on the `SELL` side:
     * - `LIMIT_MAKER/TAKE_PROFIT_LIMIT` `price` > Last Traded Price >  `STOP_LOSS/STOP_LOSS_LIMIT` `stopPrice`
     * - `TAKE_PROFIT stopPrice` > Last Traded Price > `STOP_LOSS/STOP_LOSS_LIMIT stopPrice`
     * - If the OCO is on the `BUY` side:
     * - `LIMIT_MAKER/TAKE_PROFIT_LIMIT price` < Last Traded Price < `stopPrice`
     * - `TAKE_PROFIT stopPrice` < Last Traded Price < `STOP_LOSS/STOP_LOSS_LIMIT stopPrice` * OCOs add **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     * - OCOs add 2 orders to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary New Order list - OCO (TRADE)
     * @param {OrderListOcoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderListOcoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-list-oco Binance API Documentation}
     */
    orderListOco(
        requestParameters: OrderListOcoRequest
    ): Promise<RestApiResponse<OrderListOcoResponse>> {
        return this.tradeApi.orderListOco(requestParameters);
    }

    /**
     * Place an [OPO](/products/spot/faqs/opo).
     *
     * - OPOs add 2 orders to the `EXCHANGE_MAX_NUM_ORDERS`` filter and `MAX_NUM_ORDERS`` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary New Order List - OPO (TRADE)
     * @param {OrderListOpoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderListOpoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-list-opo Binance API Documentation}
     */
    orderListOpo(
        requestParameters: OrderListOpoRequest
    ): Promise<RestApiResponse<OrderListOpoResponse>> {
        return this.tradeApi.orderListOpo(requestParameters);
    }

    /**
     * Place an [OPOCO](/products/spot/faqs/opo).
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary New Order List - OPOCO (TRADE)
     * @param {OrderListOpocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderListOpocoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-list-opoco Binance API Documentation}
     */
    orderListOpoco(
        requestParameters: OrderListOpocoRequest
    ): Promise<RestApiResponse<OrderListOpocoResponse>> {
        return this.tradeApi.orderListOpoco(requestParameters);
    }

    /**
     * Place an OTO.
     *
     * - An OTO (One-Triggers-the-Other) is an order list comprised of 2 orders.
     * - The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * - The second order is called the **pending order**. It can be any order type except for `MARKET` orders using parameter `quoteOrderQty`. The pending order is only placed on the order book when the working order gets **fully filled**.
     * - If either the working order or the pending order is cancelled individually, the other order in the order list will also be canceled or expired.
     * - When the order list is placed, if the working order gets **immediately fully filled**, the placement response will show the working order as `FILLED` but the pending order will still appear as `PENDING_NEW`. You need to query the status of the pending order again to see its updated status.
     * - OTOs add **2 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingType` or `workingType`**
     *
     * Depending on the `pendingType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                  |Additional mandatory parameters|Additional information|
     * |----                                                  |----                           |------
     * |`workingType` = `LIMIT`                               |`workingTimeInForce`           |
     * |`pendingType` = `LIMIT`                                |`pendingPrice`, `pendingTimeInForce`          |
     * |`pendingType` = `STOP_LOSS` or `TAKE_PROFIT`           |`pendingStopPrice` and/or `pendingTrailingDelta`|
     * |`pendingType` = `STOP_LOSS_LIMIT` or `TAKE_PROFIT_LIMIT`|`pendingPrice`, `pendingStopPrice` and/or `pendingTrailingDelta`, `pendingTimeInForce`|
     *
     * @summary New Order list - OTO (TRADE)
     * @param {OrderListOtoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderListOtoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-list-oto Binance API Documentation}
     */
    orderListOto(
        requestParameters: OrderListOtoRequest
    ): Promise<RestApiResponse<OrderListOtoResponse>> {
        return this.tradeApi.orderListOto(requestParameters);
    }

    /**
     * Place an OTOCO.
     *
     * - An OTOCO (One-Triggers-One-Cancels-the-Other) is an order list comprised of 3 orders.
     * - The first order is called the **working order** and must be `LIMIT` or `LIMIT_MAKER`. Initially, only the working order goes on the order book.
     * - The behavior of the working order is the same as the [OTO](#order-list-oto).
     * - OTOCO has 2 pending orders (pending above and pending below), forming an OCO pair. The pending orders are only placed on the order book when the working order gets **fully filled**.
     * - The rules of the pending above and pending below follow the same rules as the [Order list OCO](#order-list-oco).
     * - OTOCOs add **3 orders** to the `EXCHANGE_MAX_NUM_ORDERS` filter and `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 3
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Mandatory parameters based on `pendingAboveType`, `pendingBelowType` or `workingType`**
     *
     * Depending on the `pendingAboveType`/`pendingBelowType` or `workingType`, some optional parameters will become mandatory.
     *
     * |Type                                                       |Additional mandatory parameters|Additional information|
     * |----                                                       |----                           |------
     * |`workingType` = `LIMIT`                                    |`workingTimeInForce`           |
     * |`pendingAboveType`= `LIMIT_MAKER`                                |`pendingAbovePrice`     |
     * |`pendingAboveType` = `STOP_LOSS/TAKE_PROFIT`        |`pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`|
     * |`pendingAboveType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT` |`pendingAbovePrice`, `pendingAboveStopPrice` and/or `pendingAboveTrailingDelta`, `pendingAboveTimeInForce`|
     * |`pendingBelowType`= `LIMIT_MAKER`                                |`pendingBelowPrice`          |
     * |`pendingBelowType= STOP_LOSS/TAKE_PROFIT`         |`pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`|
     * |`pendingBelowType=STOP_LOSS_LIMIT/TAKE_PROFIT_LIMIT` |`pendingBelowPrice`, `pendingBelowStopPrice` and/or `pendingBelowTrailingDelta`, `pendingBelowTimeInForce`|
     *
     * @summary New Order list - OTOCO (TRADE)
     * @param {OrderListOtocoRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderListOtocoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-list-otoco Binance API Documentation}
     */
    orderListOtoco(
        requestParameters: OrderListOtocoRequest
    ): Promise<RestApiResponse<OrderListOtocoResponse>> {
        return this.tradeApi.orderListOtoco(requestParameters);
    }

    /**
     * Send in a new OCO.
     *
     * - Price Restrictions:
     * - `SELL`: Limit Price > Last Price > Stop Price
     * - `BUY`: Limit Price < Last Price < Stop Price
     * - Quantity Restrictions:
     * - Both legs must have the same quantity.
     * - `ICEBERG` quantities however do not have to be the same
     * - `OCO` adds **2 orders** to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 2
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     * @summary New OCO - Deprecated (TRADE)
     * @param {OrderOcoRequest} requestParameters Request parameters.
     * @deprecated
     * @returns {Promise<RestApiResponse<OrderOcoResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-oco Binance API Documentation}
     */
    orderOco(requestParameters: OrderOcoRequest): Promise<RestApiResponse<OrderOcoResponse>> {
        return this.tradeApi.orderOco(requestParameters);
    }

    /**
     * Test new order creation and signature/recvWindow long.
     *
     * Creates and validates a new order but does not send it into the matching
     * engine.
     *
     * Weight: |Condition|Weight|
     * |---|---|
     * |Without `computeCommissionRates`|1|
     * |With `computeCommissionRates`|20|
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order (TRADE)
     * @param {OrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<OrderTestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#order-test Binance API Documentation}
     */
    orderTest(requestParameters: OrderTestRequest): Promise<RestApiResponse<OrderTestResponse>> {
        return this.tradeApi.orderTest(requestParameters);
    }

    /**
     * Places an order using smart order routing (SOR).
     *
     * This adds 1 order to the `EXCHANGE_MAX_ORDERS` filter and the `MAX_NUM_ORDERS` filter.
     *
     * Read [SOR FAQ](/products/spot/faqs/sor_faq) to learn more.
     *
     * Weight(IP): 1
     *
     * Unfilled Order Count: 1
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Matching Engine
     *
     **Note:** `POST /api/v3/sor/order` only supports `LIMIT` and `MARKET` orders. `quoteOrderQty` is not supported.
     *
     * @summary New order using SOR (TRADE)
     * @param {SorOrderRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<SorOrderResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#sor-order Binance API Documentation}
     */
    sorOrder(requestParameters: SorOrderRequest): Promise<RestApiResponse<SorOrderResponse>> {
        return this.tradeApi.sorOrder(requestParameters);
    }

    /**
     * Test new order creation and signature/recvWindow using smart order routing (SOR).
     * Creates and validates a new order but does not send it into the matching engine.
     *
     * Weight: |Condition|Weight|
     * |---|---|
     * |Without `computeCommissionRates`|1|
     * |With `computeCommissionRates`|20|
     *
     * Security Type: TRADE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Test new order using SOR (TRADE)
     * @param {SorOrderTestRequest} requestParameters Request parameters.
     *
     * @returns {Promise<RestApiResponse<SorOrderTestResponse>>}
     * @throws {RequiredError | ConnectorClientError | UnauthorizedError | ForbiddenError | TooManyRequestsError | RateLimitBanError | ServerError | NotFoundError | NetworkError | BadRequestError}
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/rest-api/trade#sor-order-test Binance API Documentation}
     */
    sorOrderTest(
        requestParameters: SorOrderTestRequest
    ): Promise<RestApiResponse<SorOrderTestResponse>> {
        return this.tradeApi.sorOrderTest(requestParameters);
    }
}
